/**
 * AlertRecord — one alert, laid out as the record an operator works it from.
 *
 *   ┌ severity · status · Alert #N · 15h ago ─────────────── ‹ 17 of 25 › · Full page · ✕ ┐
 *   │ Alert name                                                                            │
 *   │ camera · when · event_type                                                            │
 *   ├──────────────────────────────────────────────┬────────────────────────────────────────┤
 *   │ frame  (Frame | Clip ±10s · Go live · Download) │ Detected → Acknowledged → Assigned → … │
 *   │ why this alert fired                          │ details · workflow history / comments  │
 *   ├──────────────────────────────────────────────┴────────────────────────────────────────┤
 *   │ who has it                                  Assign G · False positive F · Resolve R · … │
 *   └────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Presentational only. Each app's AlertDetailView fetches the alert, its frames and its
 * activity over its own transport (direct on a node, through the tunnel from the cloud) and
 * hands them in — so the two records cannot drift into two layouts of the same thing.
 *
 * The header is the record's own rather than the Modal's: it needs the loaded alert (severity,
 * status, the time it fired), which the list or board that opened the modal does not have.
 * Callers put their pager and "Full page" in `headerActions` and their close in `onClose`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  VideoCameraIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SnapshotGallery, { type GalleryImage } from '../common/SnapshotGallery';
import SearchPlaybackPanel from '../search/SearchPlaybackPanel';
import AlertTriggerDetails, { alertNumber, confidenceConsidered } from '../alertRules/AlertTriggerDetails';
import { toneVar } from '../common/StatusTag';
import { alertSeverityTone, alertStatusTone } from './AlertTags';
import { alertName, alertTimestampMs } from './alertVocab';
import { Avatar } from './Avatar';
import { causeLabel } from '../alertActions/alertActionsModel';
import type { RecordingSegment } from '../../api/contracts';

// ─── time and colour ────────────────────────────────────────────────────────────────────────

const mix = (color: string, pct: number, over = 'transparent') => `color-mix(in srgb, ${color} ${pct}%, ${over})`;
/** A tone darkened toward the text colour, so it stays legible as TEXT on a pale surface. */
const ink = (color: string) => mix(color, 78, 'var(--foreground)');

/** "Oct 21, 2026 · 7:28:08 PM" */
function fullStamp(ts: any): string {
  const ms = alertTimestampMs(ts);
  if (Number.isNaN(ms)) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
}

/** "7:28 PM" */
function shortTime(ts: any): string {
  const ms = alertTimestampMs(ts);
  return Number.isNaN(ms) ? '' : new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** "Oct 21, 7:28 pm" — the workflow-history sub-line. */
function histStamp(ts: any): string {
  const ms = alertTimestampMs(ts);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()}`;
}

/** "15h ago"; an absolute date once it is over a week old. */
function ago(ts: any): string {
  const ms = alertTimestampMs(ts);
  if (Number.isNaN(ms)) return '';
  const secs = Math.max(0, (Date.now() - ms) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

/** Where the alert is in its lifecycle — read the same way by the stepper and the action bar. */
function lifecycle(alert: any) {
  const status = (alert?.status || 'new') as string;
  const cancelled = status === 'cancelled';
  const resolved = status === 'resolved';
  const terminal = cancelled || resolved;
  const acked = !!alert?.acknowledged_by || status === 'acknowledged' || status === 'assigned' || terminal;
  const assigned = !!alert?.assigned_to;
  return { status, cancelled, resolved, terminal, acked, assigned };
}

type NameOf = (id: any) => string | null;

// ─── header ─────────────────────────────────────────────────────────────────────────────────

/**
 * Styles for a button in the header's action row (the pager is RecordPager `variant="boxed"`),
 * so every host's "Full page" matches the close button beside it.
 */
export const ALERT_HEADER_BUTTON =
  'flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-3.5 text-[14px] font-medium text-foreground transition-colors hover:bg-muted';

/**
 * A "Full page" button for a record header. `what` names the page in the tooltip — the alert
 * and event records share the header, and the button should say which page it opens.
 */
export function RecordFullPageButton({ onClick, what = 'record' }: { onClick: () => void; what?: string }) {
  return (
    <button type="button" onClick={onClick} title={`Open the full ${what} page`} className={ALERT_HEADER_BUTTON}>
      <ArrowTopRightOnSquareIcon className="h-4 w-4" /> Full page
    </button>
  );
}

/** The "Full page" button every alert host puts in the header. */
export function AlertFullPageButton({ onClick }: { onClick: () => void }) {
  return <RecordFullPageButton onClick={onClick} what="alert" />;
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      title="Close (Esc)"
      className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <XMarkIcon className="h-5 w-5" />
    </button>
  );
}

/** The record header's first pill — severity on an alert, the model on an event. */
export function RecordTintPill({ color, icon, children }: { color: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-bold uppercase tracking-[0.1em]"
      style={{ backgroundColor: mix(color, 14), color: ink(color) }}
    >
      {icon}
      {children}
    </span>
  );
}

/** The record header's outlined pill — status on an alert, the event type on an event. */
export function RecordOutlinePill({ dot, children }: { dot?: string; children: ReactNode }) {
  return (
    <span className="inline-flex h-8 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-foreground">
      {dot && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dot }} />}
      {children}
    </span>
  );
}

export interface RecordHeaderProps {
  /** Null while loading (or when the record failed to load): skeleton lines unless `title` is set. */
  ready: boolean;
  /** The colour bar across the top — severity on an alert. */
  stripe?: string;
  pills?: ReactNode;
  /** "Alert #12" / "Event #4821" — before the relative time. */
  reference?: string | null;
  title?: ReactNode;
  cameraName?: string;
  /** When it happened: the relative time beside the pills and the full stamp under the title. */
  timestamp?: any;
  /** A quiet identifier at the end of the camera/time line (the event type). */
  mono?: string | null;
  actions?: ReactNode;
  onClose?: () => void;
}

/**
 * The header the alert and event records share: stripe, pills, "#N · 15h ago", the name, and
 * camera · time — with the host's pager / "Full page" and the close button on the right.
 */
export function RecordHeader({ ready, stripe, pills, reference, title, cameraName, timestamp, mono, actions, onClose }: RecordHeaderProps) {
  const when = timestamp ? ago(timestamp) : '';
  return (
    /* Sticky inside the modal's scrolling body: the name and the pager stay in view while
       the record underneath is read. Opaque, since the body scrolls behind it. */
    <div className="sticky top-0 z-20 shrink-0 border-b border-border bg-card">
      <div className="h-1" style={{ backgroundColor: ready && stripe ? stripe : 'var(--border)' }} />
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 px-6 pb-4 pt-4">
        <div className="min-w-0 flex-1">
          {ready ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {pills}
                {(reference || when) && (
                  <span className="ml-1 text-[14px] text-muted-foreground">
                    {[reference, when].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              <h2 className="mt-2 truncate text-[24px] font-bold leading-tight tracking-[-0.01em] text-foreground">
                {title}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-[15px]">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <VideoCameraIcon className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  <span className="truncate font-semibold text-brand">{cameraName || '—'}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  <ClockIcon className="h-[18px] w-[18px] text-muted-foreground" />
                  {fullStamp(timestamp)}
                </span>
                {mono && <span className="font-mono text-[14px] text-muted-foreground">{mono}</span>}
              </div>
            </>
          ) : title ? (
            <h2 className="truncate py-1 text-[24px] font-bold leading-tight tracking-[-0.01em] text-foreground">{title}</h2>
          ) : (
            <div className="space-y-2.5 py-1">
              <div className="h-7 w-48 animate-pulse rounded-lg bg-muted" />
              <div className="h-7 w-72 animate-pulse rounded-lg bg-muted" />
              <div className="h-5 w-96 max-w-full animate-pulse rounded-lg bg-muted" />
            </div>
          )}
        </div>
        {(actions || onClose) && (
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            {onClose && <CloseButton onClose={onClose} />}
          </div>
        )}
      </div>
    </div>
  );
}

export function AlertRecordHeader({
  alert,
  cameraName,
  actions,
  onClose,
}: {
  /** Null while loading: the header still renders its actions and close, so the modal can be left. */
  alert: any | null;
  cameraName?: string;
  actions?: ReactNode;
  onClose?: () => void;
}) {
  const sev = toneVar(alertSeverityTone(alert?.severity));
  const num = alert ? alertNumber(alert) : null;
  const status = alert?.status || 'new';

  return (
    <RecordHeader
      ready={!!alert}
      stripe={sev}
      pills={alert && (
        <>
          <RecordTintPill color={sev} icon={<ExclamationTriangleIcon className="h-4 w-4" />}>
            {alert.severity || 'normal'}
          </RecordTintPill>
          <RecordOutlinePill dot={toneVar(alertStatusTone(alert.status))}>
            {status === 'cancelled' ? 'False positive' : status}
          </RecordOutlinePill>
          {/* The site's escalation told level N because nobody acknowledged in time. */}
          {(alert.escalation_level || 0) > 1 && (
            <RecordOutlinePill dot={toneVar(alert.escalation_state === 'ended' ? 'danger' : 'warning')}>
              Escalated · L{alert.escalation_level}{alert.escalation_state === 'ended' ? ' · not acknowledged' : ''}
            </RecordOutlinePill>
          )}
          {/* Repeats inside the rule's cooldown, counted on this alert instead of raised again. */}
          {(alert.occurrences || 0) > 1 && (
            <RecordOutlinePill dot={toneVar('info')}>Seen {alert.occurrences} times</RecordOutlinePill>
          )}
        </>
      )}
      reference={num !== null ? `Alert #${num}` : null}
      title={alert ? alertName(alert) : undefined}
      cameraName={cameraName || alert?.camera_id}
      timestamp={alert?.event_timestamp || alert?.created_at}
      mono={alert?.event_type}
      actions={actions}
      onClose={onClose}
    />
  );
}

// ─── lifecycle stepper ──────────────────────────────────────────────────────────────────────

/**
 * Detected → Acknowledged → Assigned → Resolved, with who and when under each step taken and
 * the next one ringed. Replaces six "Acknowledged by / Assigned by / Resolved by" cells that
 * were mostly "—": the order of the steps IS the information, and a grid of cells hid it.
 */
export function AlertLifecycleStepper({ alert, nameOf }: { alert: any; nameOf: NameOf }) {
  const lc = lifecycle(alert);
  const who = (id: any, at: any) => [id ? nameOf(id) : null, at ? shortTime(at) : null].filter(Boolean).join(' · ');

  const steps = [
    { label: 'Detected', done: true, sub: shortTime(alert.event_timestamp) || '—', next: '' },
    { label: 'Acknowledged', done: lc.acked, sub: who(alert.acknowledged_by, alert.acknowledged_at) || 'Done', next: 'Next step' },
    { label: 'Assigned', done: lc.assigned, sub: lc.assigned ? (nameOf(alert.assigned_to) || 'Assigned') : 'Unassigned', next: 'Unassigned' },
    lc.cancelled
      ? { label: 'False positive', done: true, sub: who(alert.cancelled_by, alert.cancelled_at) || 'Closed', next: '', bad: true }
      : { label: 'Resolved', done: lc.resolved, sub: who(alert.resolved_by, alert.resolved_at) || 'Closed', next: 'Open' },
  ] as { label: string; done: boolean; sub: string; next: string; bad?: boolean }[];

  const current = lc.terminal ? -1 : steps.findIndex((s) => !s.done);
  const success = toneVar('success');
  const danger = toneVar('danger');

  return (
    <ol className="grid grid-cols-4">
      {steps.map((s, i) => {
        const isCurrent = i === current;
        return (
          <li key={s.label} className="min-w-0">
            <div className="flex items-center">
              {s.done ? (
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ backgroundColor: s.bad ? danger : success }}
                >
                  {s.bad ? <XMarkIcon className="h-3.5 w-3.5" strokeWidth={3} /> : <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
              ) : (
                <span
                  className="h-6 w-6 shrink-0 rounded-full border-2 bg-card"
                  style={{ borderColor: isCurrent ? 'var(--brand)' : 'var(--border)' }}
                />
              )}
              {i < steps.length - 1 && (
                <span
                  className="mx-1.5 h-0.5 flex-1 rounded-full"
                  style={{ backgroundColor: s.done ? success : 'var(--border)' }}
                />
              )}
            </div>
            <div className={`mt-2 truncate pr-2 text-[15px] font-semibold ${s.done || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
              {s.label}
            </div>
            <div
              className={`truncate pr-2 text-[13px] ${isCurrent ? 'text-brand' : 'text-muted-foreground'}`}
              title={s.done ? s.sub : undefined}
            >
              {s.done ? s.sub : isCurrent && s.next ? s.next : s.next || '—'}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── facts ──────────────────────────────────────────────────────────────────────────────────

export interface AlertFact {
  label: string;
  value: ReactNode;
  /** `brand` for the facts that point somewhere (camera, time); `mono` for identifiers. */
  tone?: 'brand' | 'mono';
}

/** The alert's facts as ruled label/value rows. */
export function AlertFactList({ items }: { items: (AlertFact | false | null | undefined)[] }) {
  const rows = items.filter(Boolean) as AlertFact[];
  return (
    <dl>
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-[minmax(6rem,9rem)_minmax(0,1fr)] gap-3 border-b border-border py-2.5">
          <dt className="text-[15px] text-muted-foreground">{r.label}</dt>
          <dd
            className={`min-w-0 break-words text-[15px] font-medium ${
              r.tone === 'brand' ? 'text-brand' : r.tone === 'mono' ? 'font-mono text-[14px] text-foreground' : 'text-foreground'
            }`}
          >
            {r.value === null || r.value === undefined || r.value === '' ? '—' : r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ─── activity ───────────────────────────────────────────────────────────────────────────────

const HISTORY_TITLE: Record<string, string> = {
  created: 'Detection created',
  acknowledged: 'Acknowledged',
  unacknowledged: 'Reopened',
  assigned: 'Assigned',
  resolved: 'Resolved',
  cancelled: 'Marked false positive',
  reopened: 'Reopened',
  // Written by the site's escalation (routing policies) when nobody acknowledged in time.
  escalated: 'Escalated',
  reminded: 'Reminder sent',
  escalation_ended: 'Escalation ended — not acknowledged',
  // Notes from Alert Actions: a pause held it back; nobody on the matching notification could be reached.
  silenced: 'Not sent — notifications paused',
  rerouted: 'Sent to "Everything else" instead',
};
const HISTORY_TONE: Record<string, Parameters<typeof toneVar>[0]> = {
  created: 'info',
  acknowledged: 'warning',
  unacknowledged: 'info',
  assigned: 'brand',
  resolved: 'success',
  cancelled: 'danger',
  reopened: 'info',
  escalated: 'warning',
  reminded: 'warning',
  escalation_ended: 'danger',
  silenced: 'info',
  rerouted: 'warning',
};

export interface AlertActivityPanelProps {
  alert: any;
  nameOf: NameOf;
  currentUserName?: string | null;
  /** The alert's audit rows (lifecycle actions + comments), newest or oldest first. */
  loadActions: () => Promise<any[]>;
  addComment: (text: string) => Promise<unknown>;
  className?: string;
}

/**
 * Workflow history and comments, as two tabs over one list — and the note box BELOW both,
 * always: writing a note for the next shift is the one thing done here on every alert, and it
 * should not depend on which tab happens to be open.
 */
export function AlertActivityPanel({ alert, nameOf, currentUserName, loadActions, addComment, className = '' }: AlertActivityPanelProps) {
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'history' | 'comments'>('history');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  // The loaders are closures the host rebuilds every render; held in refs so a re-render is
  // not a refetch.
  const loadRef = useRef(loadActions);
  loadRef.current = loadActions;
  const addRef = useRef(addComment);
  addRef.current = addComment;

  const load = useCallback(() => {
    loadRef.current()
      .then((rows) => setActions(Array.isArray(rows) ? rows : []))
      .catch(() => setActions([]))
      .finally(() => setLoading(false));
  }, []);

  // Refetch when the alert row itself changes, so an ack/assign/resolve done from the action
  // bar shows in the history without a reload.
  const alertVersion = [alert?.id, alert?.status, alert?.updated_at, alert?.assigned_to, alert?.acknowledged_by].join('|');
  useEffect(() => { load(); }, [load, alertVersion]);

  const post = async () => {
    const text = comment.trim();
    if (!text || posting) return;
    setPosting(true);
    setError('');
    try {
      await addRef.current(text);
      setComment('');
      setTab('comments');
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to post the note');
    } finally {
      setPosting(false);
    }
  };

  const at = (r: any) => alertTimestampMs(r?.created_at);
  const byTime = (a: any, b: any) => at(a) - at(b);
  // The detection itself is not an audit row — it is synthesised as the first entry.
  const history = [
    { id: '__created', action: 'created', actor_name: 'System', created_at: alert?.event_timestamp || alert?.created_at },
    ...actions.filter((a) => a.action !== 'commented').sort(byTime),
  ];
  const comments = actions.filter((a) => a.action === 'commented').sort(byTime);
  const actor = (r: any) => r.actor_name || (r.actor_id ? nameOf(r.actor_id) : null) || 'Unknown';

  // A render function, not an inner component: a component type rebuilt every render would
  // remount the tabs on each keystroke in the note box.
  const tabButton = (id: 'history' | 'comments', label: string, count: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 text-[16px] font-semibold transition-colors ${
        tab === id ? 'border-brand text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      <span className="rounded-md bg-muted px-1.5 text-[12px] font-semibold tabular-nums text-muted-foreground">{count}</span>
    </button>
  );

  return (
    <div data-tour="alert-detail-activity" className={`flex min-h-0 flex-col ${className}`}>
      <div role="tablist" className="flex items-center gap-8 border-b border-border">
        {tabButton('history', 'Workflow history', history.length)}
        {tabButton('comments', 'Comments', comments.length)}
      </div>

      {/* The one unbounded part of the record — it scrolls inside itself. */}
      <div className="min-h-[7rem] flex-1 overflow-y-auto pr-1 pt-4" style={{ maxHeight: '30vh' }}>
        {tab === 'history' ? (
          loading && history.length === 1 ? (
            <p className="text-[13px] text-muted-foreground">Loading activity…</p>
          ) : (
            <ul className="space-y-3.5">
              {history.map((r, i) => (
                <li key={r.id} className="relative pl-8">
                  {i < history.length - 1 && <span className="absolute bottom-[-0.875rem] left-[5px] top-5 w-px bg-border" />}
                  <span
                    className="absolute left-0 top-[7px] h-3 w-3 rounded-full"
                    style={{ backgroundColor: toneVar(HISTORY_TONE[r.action] || 'neutral') }}
                  />
                  <p className="text-[16px] font-semibold text-foreground">
                    {HISTORY_TITLE[r.action] || r.action}
                    {r.action === 'assigned' && <> → {r.target_name || nameOf(r.target_id) || 'operator'}</>}
                  </p>
                  <p className="text-[14px] text-muted-foreground">{actor(r)} · {histStamp(r.created_at)}</p>
                  {r.note && (
                    <p className="mt-1 whitespace-pre-wrap rounded-lg bg-muted px-2.5 py-1.5 text-[13px] text-foreground">{r.note}</p>
                  )}
                </li>
              ))}
            </ul>
          )
        ) : comments.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No notes yet — add the first one below.</p>
        ) : (
          <ul className="space-y-3.5">
            {comments.map((c) => (
              <li key={c.id} className="flex gap-3">
                <Avatar name={actor(c)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[14px] font-semibold text-foreground">{actor(c)}</span>
                    <span className="text-[12px] text-muted-foreground">{ago(c.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-[14px] text-muted-foreground">{c.note}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-auto pt-4">
        <div className="flex items-center gap-3">
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); post(); } }}
            placeholder="Add a note for the next shift…"
            aria-label={currentUserName ? `Add a note as ${currentUserName}` : 'Add a note'}
            className="h-12 min-w-0 flex-1 rounded-xl border border-border bg-background px-4 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
          <button
            type="button"
            onClick={post}
            disabled={!comment.trim() || posting}
            className="h-12 rounded-xl border border-border bg-card px-5 text-[15px] font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            {posting ? 'Posting…' : 'Post'}
          </button>
        </div>
        {error && <p className="mt-1.5 text-[12px] text-destructive">{error}</p>}
      </div>
    </div>
  );
}

// ─── action bar ─────────────────────────────────────────────────────────────────────────────

type BarTone = 'neutral' | 'danger' | 'success' | 'primary';

function BarButton({ label, hint, icon, tone, onClick }: { label: string; hint: string; icon: ReactNode; tone: BarTone; onClick: () => void }) {
  const primary = tone === 'primary';
  const color = tone === 'danger' ? ink(toneVar('danger')) : tone === 'success' ? ink(toneVar('success')) : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} (${hint})`}
      className={`flex h-12 items-center gap-2 rounded-xl border px-4 text-[15px] font-semibold transition-colors ${
        primary ? 'border-transparent bg-brand text-white hover:bg-brand-dark' : 'border-border bg-card text-foreground hover:bg-muted'
      }`}
      style={color ? { color } : undefined}
    >
      {icon}
      {label}
      <kbd
        className={`ml-1 rounded-md px-1.5 py-0.5 font-sans text-[11px] font-semibold ${primary ? 'text-white' : 'border border-border text-muted-foreground'}`}
        style={primary ? { backgroundColor: 'rgba(255,255,255,0.22)' } : undefined}
      >
        {hint}
      </kbd>
    </button>
  );
}

export interface AlertActionBarProps {
  alert: any;
  nameOf: NameOf;
  /** Without alerts.edit the bar still says who has the alert, with no buttons. */
  canManage: boolean;
  /** Off while one of the record's own dialogs (assign, resolve note) is open. */
  shortcuts?: boolean;
  error?: string;
  onAck: () => void;
  onAssign: () => void;
  onResolve: () => void;
  onCancel: () => void;
  onReopen: () => void;
}

/**
 * The lifecycle actions, pinned across the foot of the record, with the step that comes next
 * filled in brand — and a sentence on the left saying who has the alert, which is the question
 * the buttons answer. Each action has a single-key shortcut (shown on the button), ignored
 * while typing.
 */
export function AlertActionBar({ alert, nameOf, canManage, shortcuts = true, error, onAck, onAssign, onResolve, onCancel, onReopen }: AlertActionBarProps) {
  const lc = lifecycle(alert);
  const by = (id: any) => (id ? ` by ${nameOf(id) || 'an operator'}` : '');
  const when = (ts: any) => (ts ? ` · ${ago(ts)}` : '');

  const sentence = lc.cancelled
    ? `Marked false positive${by(alert.cancelled_by)}${when(alert.cancelled_at)}.`
    : lc.resolved
      ? `Resolved${by(alert.resolved_by)}${when(alert.resolved_at)}.`
      : lc.assigned
        ? `Assigned to ${nameOf(alert.assigned_to) || 'an operator'}${when(alert.assigned_at)}.`
        : lc.acked
          ? `Acknowledged${by(alert.acknowledged_by)}${when(alert.acknowledged_at)} — not assigned yet.`
          : 'Nobody has picked this up yet.';

  const buttons: { key: string; label: string; icon: ReactNode; tone: BarTone; run: () => void }[] = !canManage
    ? []
    : lc.terminal
      ? [{ key: 'O', label: 'Reopen', icon: <ArrowUturnLeftIcon className="h-5 w-5" />, tone: 'primary', run: onReopen }]
      : [
          ...(!lc.assigned ? [{ key: 'G', label: 'Assign', icon: <UserPlusIcon className="h-5 w-5" />, tone: 'neutral' as BarTone, run: onAssign }] : []),
          { key: 'F', label: 'False positive', icon: <XCircleIcon className="h-5 w-5" />, tone: 'danger', run: onCancel },
          { key: 'R', label: 'Resolve', icon: <CheckCircleIcon className="h-5 w-5" />, tone: lc.acked ? 'primary' : 'success', run: onResolve },
          ...(!lc.acked ? [{ key: 'A', label: 'Acknowledge', icon: <CheckIcon className="h-5 w-5" />, tone: 'primary' as BarTone, run: onAck }] : []),
        ];

  // Latest buttons in a ref, so the listener is bound once per enable/disable rather than on
  // every render.
  const buttonsRef = useRef(buttons);
  buttonsRef.current = buttons;
  useEffect(() => {
    if (!shortcuts || !canManage) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const hit = buttonsRef.current.find((b) => b.key.toLowerCase() === e.key.toLowerCase());
      if (!hit) return;
      e.preventDefault();
      hit.run();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcuts, canManage]);

  return (
    <div
      data-tour="alert-detail-actions"
      className="sticky bottom-0 z-20 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-3 border-t border-border px-6 py-4"
      style={{ backgroundColor: mix('var(--muted)', 35, 'var(--card)') }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[15px] text-muted-foreground">{sentence}</p>
        {error && <p className="mt-0.5 text-[13px] text-destructive">{error}</p>}
      </div>
      {buttons.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {buttons.map((b) => (
            <BarButton key={b.key} label={b.label} hint={b.key} icon={b.icon} tone={b.tone} onClick={b.run} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── media ──────────────────────────────────────────────────────────────────────────────────

/** How the host plays recorded footage of this alert's camera. Times are ISO strings. */
export interface AlertPlayback {
  url: (fromIso: string, toIso: string) => string | null;
  segments: (fromIso: string, toIso: string) => Promise<RecordingSegment[]>;
  tunnelled?: boolean;
  authToken?: () => string | null;
  /** Open the Recordings page at this window — the escape hatch past the clip. */
  openFull?: (fromIso: string, toIso: string) => void;
}

/** 10 s either side of the detection — the clip Search plays, for the same reason. */
const CLIP_LEAD_SEC = 10;
const CLIP_TAIL_SEC = 10;
/** Segments are asked for a segment-and-a-bit wider than the clip (see SearchPage). */
const SEGMENT_PAD_MS = 6 * 60 * 1000;

const safeName = (s: string) => s.replace(/[^\w.-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

/**
 * Save a frame. Fetched as a blob so the file gets a useful name (a cross-origin `download`
 * attribute is ignored); where the fetch is refused, the frame opens in a tab instead.
 */
async function downloadImage(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    const a = document.createElement('a');
    a.href = href;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** What a host-built frame viewer is handed — see AlertMedia's `renderFrame`. */
export interface RecordFrameSlot {
  frameClassName: string;
  /** Call with the frame on screen, so Download saves the one being looked at. */
  onSelectionChange: (image: GalleryImage | null) => void;
}

export interface AlertMediaProps {
  /** The alert — or any record with `id`, `camera_id` and `event_timestamp` (an AI event). */
  alert: any;
  /** What the record is called, for the clip's subtitle and the download's filename. */
  name?: string;
  cameraName?: string;
  images: GalleryImage[];
  /**
   * The host's own frame viewer in place of the plain gallery — the event record's carries
   * subject overlays and, on the cloud, streams each frame through the tunnel.
   */
  renderFrame?: ((slot: RecordFrameSlot) => ReactNode) | null;
  /** Shown in place of the frame when there is none ("Loading snapshots…", "No snapshots"). */
  emptyNote?: string;
  playback?: AlertPlayback | null;
  /** The camera's live view, when the host can play one. */
  renderLive?: (() => ReactNode) | null;
  frameClassName?: string;
}

/** The frame the alert fired on — or the footage around it, or the camera now. */
export function AlertMedia({ alert, name, cameraName, images, renderFrame, emptyNote, playback, renderLive, frameClassName = 'h-[40vh]' }: AlertMediaProps) {
  const [mode, setMode] = useState<'frame' | 'clip' | 'live'>('frame');
  // Filed under the record it was chosen in: an event record pages without remounting, and a
  // next record with no frames must not offer to download the previous record's.
  const [picked, setPicked] = useState<{ rec: any; img: GalleryImage | null }>({ rec: null, img: null });
  const selected = picked.rec === alert?.id ? picked.img : null;
  const setSelected = (img: GalleryImage | null) => setPicked({ rec: alert?.id, img });
  const label = name ?? alertName(alert);
  // A new record starts on its frame, not on the previous record's clip or live view.
  useEffect(() => { setMode('frame'); }, [alert?.id]);

  const at = alertTimestampMs(alert?.event_timestamp);
  const clip = useMemo(() => {
    if (!playback || !alert?.camera_id || Number.isNaN(at)) return null;
    const from = at - CLIP_LEAD_SEC * 1000;
    const to = at + CLIP_TAIL_SEC * 1000;
    return {
      fromMs: from,
      toMs: to,
      fromIso: new Date(from).toISOString(),
      toIso: new Date(to).toISOString(),
    };
  }, [playback, alert?.camera_id, at]);
  const clipSrc = useMemo(() => (clip && playback ? playback.url(clip.fromIso, clip.toIso) : null), [clip, playback]);

  // A host-built viewer reports its own selection; the plain gallery's is checked against the list.
  const current = renderFrame
    ? (selected?.url ? selected : images[0] || null)
    : selected && images.some((i) => i.url === selected.url) ? selected : images[0] || null;

  const segBtn = (on: boolean) =>
    `px-5 text-[15px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
      on ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'
    }`;

  return (
    <div className="min-w-0">
      {mode === 'clip' && clip && playback ? (
        <SearchPlaybackPanel
          key={`${alert.id}-clip`}
          inline
          frameClassName={frameClassName}
          title={cameraName || alert.camera_id}
          subtitle={label}
          src={clipSrc}
          leadSeconds={CLIP_LEAD_SEC}
          tailSeconds={CLIP_TAIL_SEC}
          eventMs={at}
          windowMs={{ from: clip.fromMs, to: clip.toMs }}
          loadSegments={() => playback.segments(
            new Date(clip.fromMs - SEGMENT_PAD_MS).toISOString(),
            new Date(clip.toMs + SEGMENT_PAD_MS).toISOString(),
          )}
          tunnelled={playback.tunnelled}
          getAuthToken={playback.authToken}
          onOpenFull={playback.openFull ? () => playback.openFull!(clip.fromIso, clip.toIso) : undefined}
          onClose={() => setMode('frame')}
        />
      ) : mode === 'live' && renderLive ? (
        <div className={`relative overflow-hidden rounded-xl border border-border bg-black ${frameClassName}`}>
          {renderLive()}
          <span className="pointer-events-none absolute left-3 top-3 flex items-center gap-1.5 rounded-md bg-black/75 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> Live
          </span>
        </div>
      ) : renderFrame ? (
        renderFrame({ frameClassName, onSelectionChange: setSelected })
      ) : images.length > 0 ? (
        <SnapshotGallery
          images={images}
          // ← → stay with the pager stepping through RECORDS; ↑ ↓ move between frames.
          arrowKeys="updown"
          frameClassName={frameClassName}
          sizeCorner
          onSelectionChange={(img) => setSelected(img)}
        />
      ) : (
        <div className={`flex items-center justify-center rounded-xl border border-border bg-muted px-6 text-center text-[14px] text-muted-foreground ${frameClassName}`}>
          {emptyNote || 'No snapshots for this alert'}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex h-11 overflow-hidden rounded-xl border border-border" role="tablist" aria-label="What to show">
          <button type="button" role="tab" aria-selected={mode === 'frame'} onClick={() => setMode('frame')} className={segBtn(mode === 'frame')}>
            Frame
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'clip'}
            disabled={!clip}
            onClick={() => setMode('clip')}
            title={clip ? `Recorded footage, ${CLIP_LEAD_SEC}s before to ${CLIP_TAIL_SEC}s after the detection` : 'No recordings available here'}
            className={`border-l border-border ${segBtn(mode === 'clip')}`}
          >
            Clip ±{CLIP_LEAD_SEC}s
          </button>
        </div>
        <span className="flex-1" />
        {renderLive && (
          <button
            type="button"
            onClick={() => setMode(mode === 'live' ? 'frame' : 'live')}
            title={mode === 'live' ? 'Back to the frame' : 'Watch this camera live'}
            className={`flex h-11 items-center gap-2 rounded-xl border px-4 text-[15px] font-medium transition-colors ${
              mode === 'live' ? 'border-transparent bg-destructive text-white' : 'border-border bg-card text-foreground hover:bg-muted'
            }`}
          >
            <ComputerDesktopIcon className="h-5 w-5" />
            {mode === 'live' ? 'Stop live' : 'Go live'}
          </button>
        )}
        <button
          type="button"
          disabled={!current}
          onClick={() => current && downloadImage(
            current.url,
            safeName([label, cameraName, current.label, new Date(Number.isNaN(at) ? Date.now() : at).toISOString().slice(0, 19)].filter(Boolean).join('_')),
          )}
          title={current ? `Download ${current.label ? current.label.toLowerCase() : 'this frame'}` : 'No frame to download'}
          className="flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-4 text-[15px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ArrowDownTrayIcon className="h-5 w-5" /> Download
        </button>
      </div>
    </div>
  );
}

// ─── the record ─────────────────────────────────────────────────────────────────────────────

export interface AlertRecordProps {
  alert: any;
  cameraName?: string;
  userMap: Record<string, string>;
  images: GalleryImage[];
  imagesNote?: string;
  playback?: AlertPlayback | null;
  renderLive?: (() => ReactNode) | null;
  /** Pager, "Full page" — beside the close button in the header. */
  headerActions?: ReactNode;
  onClose?: () => void;
  canManage: boolean;
  /** Single-key action shortcuts; the host turns them off while its own dialogs are open. */
  shortcuts?: boolean;
  error?: string;
  currentUserName?: string | null;
  loadActions: () => Promise<any[]>;
  addComment: (text: string) => Promise<unknown>;
  onAck: () => void;
  onAssign: () => void;
  onResolve: () => void;
  onCancel: () => void;
  onReopen: () => void;
  className?: string;
}

export default function AlertRecord(p: AlertRecordProps) {
  const { alert, userMap } = p;
  const nameOf: NameOf = (id) => (id ? userMap[id] || String(id).slice(0, 8) : null);

  return (
    <div className={`flex min-h-full flex-col bg-card ${p.className || ''}`}>
      <AlertRecordHeader alert={alert} cameraName={p.cameraName} actions={p.headerActions} onClose={p.onClose} />

      {/* Evidence on the left (what was seen, and why it counted), the alert's state and its
          paper trail on the right. One column below xl, where there is no width to split. */}
      <div className="grid flex-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
        <div data-tour="alert-detail-evidence" className="min-w-0 space-y-5 px-6 py-5">
          <AlertMedia
            alert={alert}
            cameraName={p.cameraName}
            images={p.images}
            emptyNote={p.imagesNote}
            playback={p.playback}
            renderLive={p.renderLive}
          />
          <AlertTriggerDetails alert={alert} />
        </div>

        <div className="flex min-w-0 flex-col gap-6 border-t border-border px-6 py-5 xl:border-l xl:border-t-0">
          <AlertLifecycleStepper alert={alert} nameOf={nameOf} />

          <section data-tour="alert-detail-details">
            <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Details</h3>
            <AlertFactList
              items={[
                { label: 'Alert name', value: alertName(alert) },
                { label: 'Event', value: alert.event_type, tone: 'mono' },
                { label: 'Camera', value: p.cameraName || alert.camera_id, tone: 'brand' },
                { label: 'Detected', value: fullStamp(alert.event_timestamp), tone: 'brand' },
                // Only a confidence the rule judged; otherwise it is a placeholder or unread.
                confidenceConsidered(alert) && { label: 'Confidence', value: `${Math.round(Number(alert.confidence) * 100)}%` },
                !!alert.summary && { label: 'Summary', value: alert.summary, tone: 'brand' },
                !!alert.resolution_cause && { label: 'Cause', value: causeLabel(alert.resolution_cause) },
                !!alert.resolution_note && { label: 'Resolution note', value: alert.resolution_note },
                (alert.occurrences || 0) > 1 && { label: 'Seen', value: `${alert.occurrences} times${alert.last_seen_at ? ` · last ${fullStamp(alert.last_seen_at)}` : ''}` },
                !!alert.cancel_reason && { label: 'Reason', value: alert.cancel_reason },
              ]}
            />
          </section>

          <AlertActivityPanel
            className="flex-1"
            alert={alert}
            nameOf={nameOf}
            currentUserName={p.currentUserName}
            loadActions={p.loadActions}
            addComment={p.addComment}
          />
        </div>
      </div>

      <AlertActionBar
        alert={alert}
        nameOf={nameOf}
        canManage={p.canManage}
        shortcuts={p.shortcuts}
        error={p.error}
        onAck={p.onAck}
        onAssign={p.onAssign}
        onResolve={p.onResolve}
        onCancel={p.onCancel}
        onReopen={p.onReopen}
      />
    </div>
  );
}
